import * as React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { FC, useMemo } from 'react'

import { currencyString } from '@sales-center/common'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { InfoIcon, LightTooltip } from '@sales-center/components'

type MetricVariant = 'h1' | 'h2' | 'h3' | 'h4'

interface Props {
  actionBtn?: React.ReactNode
  count: number
  handleClick?: () => void
  infoText?: string
  subtitle?: React.ReactNode
  title: string
  value?: number
  variant: MetricVariant
}

const getStyles = (
  props: Props,
  countFontSize: string,
  titleTopMargin: string,
  theme: Theme,
) => ({
  count: {
    fontFamily:
      props.variant == 'h1' ? 'Source Sans Pro Semibold' : 'Source Sans Pro',
    fontSize: countFontSize,
    marginTop: theme.spacing(0),
    marginLeft: props.variant === 'h1' ? '14px' : '0px',
    lineHeight: 1,
  },

  link: {
    color: '#005AA3',
    cursor: 'pointer',
  },

  subtitle: {
    fontSize: '12px',
    marginTop: theme.spacing(0.3),
  },

  title: {
    fontFamily:
      props.variant == 'h1' ? 'Source Sans Pro Semibold' : 'Source Sans Pro',
    marginLeft: props.variant == 'h2' || props.variant == 'h3' ? '0px' : '0',
    marginTop: titleTopMargin,
    fontWeight: 'bold',
  },

  value: {
    fontSize: '18px',
    marginLeft: props.variant !== 'h1' ? '0px' : '14px',
    lineHeight: 1,
  },
})

export const SalesFunnelMetric: FC<Props> = (props) => {
  const {
    actionBtn,
    count,
    handleClick,
    infoText,
    subtitle,
    title,
    value,
    variant,
  } = props

  const countFontSize = useMemo(() => {
    switch (variant) {
      case 'h1':
        return '64px'
      default:
        return '24px'
    }
  }, [variant])

  const titleTopMargin = useMemo(() => {
    switch (variant) {
      case 'h2':
      case 'h3':
        return '28px'
      default:
        return '0px'
    }
  }, [variant])
  const styles = getStyles(props, countFontSize, titleTopMargin, useTheme())
  return (
    <>
      <Box>
        <Typography
          sx={{ display: 'flex', alignItems: 'center', ...styles.title }}
        >
          {title}{' '}
          {(variant == 'h2' || variant == 'h3') && infoText && (
            <LightTooltip title={infoText}>
              <Box
                width="15px"
                height="15px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                ml={0.2}
              >
                <InfoIcon height="12px" width="12px" />
              </Box>
            </LightTooltip>
          )}
        </Typography>
      </Box>
      <Box>
        <Typography
          sx={{ ...styles.count, ...(!!handleClick && styles.link) }}
          onClick={handleClick}
        >
          {count.toLocaleString()}
        </Typography>
        {(!!value || value == 0) && (
          <Typography sx={styles.value}>{`$${currencyString(
            value,
          )}`}</Typography>
        )}
      </Box>
      <Box>
        {subtitle && (
          <Typography sx={styles.subtitle}>
            {subtitle} {actionBtn ? actionBtn : null}
          </Typography>
        )}
      </Box>
    </>
  )
}

SalesFunnelMetric.displayName = 'SalesFunnelMetric'
